import { useEffect, useState } from 'react'
import { Bell, BellRing, Megaphone, AlertTriangle, Info, X } from 'lucide-react'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { ReactionBar } from './ReactionBar'

export type LivePushNotification = {
  id: number
  kind?: string         // 'notification' | 'alarm' | 'news'
  severity?: string     // 'info' | 'warning' | 'critical' | ''
  title?: string
  body?: string
  image_url?: string
  created_at?: number
  reactions?: Record<string, number>
  my_reaction?: string
}

type Props = {
  pending: LivePushNotification[]
  onAck: (id: number) => void
}

const SEVERITY_ICON: Record<string, any> = {
  critical: AlertTriangle,
  warning: BellRing,
  info: Info,
  notification: Bell,
  alarm: AlertTriangle,
}

const SEVERITY_TONE: Record<string, { ring: string; icon: string; badge: string; label: string }> = {
  critical: {
    ring: 'border-red-500/40',
    icon: 'text-red-400 bg-red-500/10',
    badge: 'bg-red-500/15 text-red-300 border-red-500/30',
    label: 'Критично',
  },
  warning: {
    ring: 'border-amber-500/40',
    icon: 'text-amber-400 bg-amber-500/10',
    badge: 'bg-amber-500/15 text-amber-200 border-amber-500/30',
    label: 'Внимание',
  },
  info: {
    ring: 'border-sky-500/40',
    icon: 'text-sky-400 bg-sky-500/10',
    badge: 'bg-sky-500/15 text-sky-200 border-sky-500/30',
    label: 'Информация',
  },
  default: {
    ring: 'border-border',
    icon: 'text-primary bg-primary/10',
    badge: 'bg-muted text-muted-foreground border-border',
    label: 'Уведомление',
  },
}

export default function LiveNotificationToast({ pending, onAck }: Props) {
  const current = pending[0]
  const [busy, setBusy] = useState(false)

  // Reset busy whenever a new notification surfaces.
  useEffect(() => { setBusy(false) }, [current?.id])

  if (!current) return null

  const sevKey = (current.severity || current.kind || 'info').toLowerCase()
  const tone = SEVERITY_TONE[sevKey] || SEVERITY_TONE.default
  const Icon = SEVERITY_ICON[sevKey] || (current.kind === 'news' ? Megaphone : Bell)

  const handleAck = async () => {
    if (busy) return
    setBusy(true)
    try {
      await fetch(`/api/notifications/${current.id}/read`, { method: 'POST' })
    } catch { /* ignore — onAck still removes from queue */ }
    onAck(current.id)
  }

  const remaining = pending.length - 1
  const created = current.created_at
    ? new Date((current.created_at || 0) * 1000).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
      })
    : ''

  return (
    <Dialog open={true} onOpenChange={() => { /* persistent — only «Понятно» dismisses */ }}>
      <DialogContent
        className={`sm:max-w-lg p-0 overflow-hidden border-2 ${tone.ring} [&>button]:hidden`}
        onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
      >
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border/60">
          <div className="flex items-start gap-3">
            <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full ${tone.icon}`}>
              <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="outline" className={`text-[10px] px-1.5 py-0 h-5 ${tone.badge}`}>
                  {tone.label}
                </Badge>
                {created && (
                  <span className="text-[10px] text-muted-foreground tabular-nums">
                    {created}
                  </span>
                )}
              </div>
              <DialogTitle className="text-base leading-snug">
                {current.title || 'Новое уведомление'}
              </DialogTitle>
            </div>
          </div>
          {current.body && (
            <DialogDescription className="sr-only">{current.body}</DialogDescription>
          )}
        </DialogHeader>

        <div className="px-5 py-4 max-h-[55vh] overflow-y-auto">
          {current.image_url && (
            <img
              src={current.image_url}
              alt=""
              className="mb-3 w-full max-h-48 object-contain rounded-md border border-border/40 bg-muted/30"
            />
          )}
          {current.body && (
            <div className="text-sm text-foreground/90 whitespace-pre-wrap leading-relaxed">
              {current.body}
            </div>
          )}
        </div>

        <DialogFooter className="px-5 py-3.5 border-t border-border/60 flex-col items-stretch gap-2.5 sm:flex-col sm:space-x-0">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-muted-foreground mr-1">Реакция:</span>
            <ReactionBar
              alarmId={current.id}
              reactions={current.reactions}
              myReaction={current.my_reaction}
              compact
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">
              {remaining > 0 ? `Ещё ${remaining}` : ''}
            </span>
            <Button onClick={handleAck} disabled={busy} className="gap-1.5 min-w-28">
              {busy ? '…' : (
                <>
                  <X className="h-3.5 w-3.5" />
                  Понятно
                </>
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
